import { z } from 'zod';

/**
 * Per-prompt draft persistence for in-progress AskUserQuestion answers.
 *
 * The agents page remounts the whole stream subtree when the user switches
 * sessions (`key={agent.id}`), which wipes the card's local selection state.
 * Mirroring `web`'s agent-thread draft pattern, drafts are keyed per prompt
 * (`<agentId>:<requestId>`) so switching away and back restores the user's
 * in-flight answers.
 *
 * sessionStorage (not localStorage) on purpose: a pending prompt is tied to a
 * live agent turn and never outlives the browser session, and prompts that
 * expire unanswered would otherwise pile up stale per-request keys forever.
 */

const KEY_PREFIX = 'reflex:ask-user-question-draft:';

const draftSchema = z.object({
  questionIdx: z.number().int().nonnegative(),
  /** Question index (stringified) → selected option indices. */
  selections: z.record(z.string(), z.array(z.number().int().nonnegative())),
  /** Question index (stringified) → free-text notes. */
  notes: z.record(z.string(), z.string()),
});

export type AskUserQuestionDraft = z.infer<typeof draftSchema>;

function storageKey(draftKey: string): string {
  return `${KEY_PREFIX}${draftKey}`;
}

export function loadAskUserQuestionDraft(draftKey: string): AskUserQuestionDraft | null {
  try {
    const stored = sessionStorage.getItem(storageKey(draftKey));
    if (!stored) return null;
    const parsed = draftSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : null;
  } catch (error) {
    console.warn('Failed to load AskUserQuestion draft:', error);
    return null;
  }
}

export function saveAskUserQuestionDraft(draftKey: string, draft: AskUserQuestionDraft): void {
  try {
    sessionStorage.setItem(storageKey(draftKey), JSON.stringify(draft));
  } catch (error) {
    console.warn('Failed to save AskUserQuestion draft:', error);
  }
}

export function clearAskUserQuestionDraft(draftKey: string): void {
  try {
    sessionStorage.removeItem(storageKey(draftKey));
  } catch (error) {
    console.warn('Failed to clear AskUserQuestion draft:', error);
  }
}
